import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { elapsedToString, TimeUnit } from '@sama/js-core';
import { of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PersistenceService } from '../../shared/misc-services/persistence.service';

@Component({
  selector: 'h3-last-saved-badge',
  templateUrl: './last-saved-badge.component.html',
  styleUrl: './last-saved-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LastSavedBadgeComponent {
  private readonly persistenceService = inject(PersistenceService);

  lastSave$ = this.buildLastSave$();
  retryCount$ = this.persistenceService.getRetryCount$();

  private buildLastSave$() {
    return this.persistenceService.getLastSaveTimestamp$().pipe(
      // switchMap allows to unsubscribe from previous timer
      // and start a new timer from this timestamp
      switchMap((timestamp) => {
        // timestamp is deleted when task is submitted
        // we want to emit a falsy value to hide the badge
        if (!timestamp) {
          return of(undefined);
        }
        // we need to recalculate the elapsed time every minute or else
        // the badge will always display 'Last saved 0 min ago'
        return timer(0, 60_000).pipe(
          map(() => this.convertDraftTimestampToElapsedTime(timestamp)),
        );
      }),
    );
  }

  private convertDraftTimestampToElapsedTime(timestamp: number) {
    const elapsedTime = Date.now() - timestamp;
    const timeUnits = [TimeUnit.Minutes];

    // display hours starting from 61 minutes
    if (elapsedTime >= 3_660_000) {
      timeUnits.unshift(TimeUnit.Hours);
    }

    const split = elapsedToString(elapsedTime, timeUnits).split(' ');
    const minuteIndex = split.indexOf('m');
    split[minuteIndex] = +split[minuteIndex - 1] > 1 ? 'minutes' : 'minute';

    const hourIndex = split.indexOf('h');
    if (hourIndex > 0) {
      split[hourIndex] = +split[hourIndex - 1] > 1 ? 'hours' : 'hour';
    }

    return split.join(' ');
  }
}
